import { type PlanBuildData, type PlanConfig, type PlanStop, type StopBuilder, ITEMS_PER_MINUTE } from "../plan-template.js";
import { currentRetrievability, isDue } from "../fsrs.js";

export const reviewStop: StopBuilder = (
  data: PlanBuildData,
  config: PlanConfig,
  alreadyPlanned: ReadonlySet<string>,
): PlanStop | null => {
  const { items, cards, nodes, now, sessionTargetMinutes } = data;
  const { reviewBudgetFraction, reviewLookAheadMs } = config;

  const budget = Math.round(sessionTargetMinutes * ITEMS_PER_MINUTE * reviewBudgetFraction);

  const schedulableItems = items.filter(
    (i) =>
      data.unlockedNodeSet.has(i.nodeId) &&
      data.engagedNodeSet.has(i.nodeId) &&
      data.practiceReviewedNodeSet.has(i.nodeId),
  );

  const lookAheadCutoff = new Date(now.getTime() + reviewLookAheadMs);

  const due = schedulableItems
    .filter((i) => {
      if (alreadyPlanned.has(i.id)) return false;
      const c = cards[i.id];
      return c !== undefined && c.state !== "new" && isDue(c, lookAheadCutoff);
    })
    .map((i) => ({ item: i, r: currentRetrievability(cards[i.id]!, now) }))
    .sort((a, b) => a.r - b.r)
    .map((x) => x.item)
    .slice(0, budget);

  if (due.length === 0) return null;

  // Separate items that are strictly due today from those pulled in by look-ahead.
  const strictlyDueCount = due.filter((i) => isDue(cards[i.id]!, now)).length;
  const hasLookAheadItems = strictlyDueCount < due.length;

  const nodeCount = new Map<string, number>();
  for (const item of due) {
    nodeCount.set(item.nodeId, (nodeCount.get(item.nodeId) ?? 0) + 1);
  }
  const topTitles = [...nodeCount.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([nodeId]) => nodes.find((n) => n.id === nodeId)?.title ?? nodeId);

  const title = hasLookAheadItems && strictlyDueCount === 0
    ? "Coming due soon"
    : "Due queue";
  const reason = hasLookAheadItems && strictlyDueCount === 0
    ? `${topTitles.join(" · ")} — due within 48 h, reviewing slightly ahead.`
    : `${topTitles.join(" · ")} — scheduled by lowest recall probability.`;

  return {
    kind: "review",
    title,
    reason,
    itemCount: due.length,
    estimatedMinutes: Math.max(1, Math.round(due.length / ITEMS_PER_MINUTE)),
    items: due,
  };
};
